import { Fragment, type ReactNode } from 'react';

import type { JSONContent } from '@tiptap/core';
import { produce } from 'immer';

import { useDreamComponentsContext } from '@/dreamComponentProvider/hooks';
import { ContentTags } from '@/postElement/SerializerV2/components/ContentTags';
import { PaidContent } from '@/postElement/SerializerV2/components/PaidContent';
import type { SerializableNode, SerializerProps } from '@/serializer';
import type { Breadcrumb, PostHeaderData, UTMAttrs } from '@/types';
import { formatPostTime, updateText } from '@/utils/post/utils';

import { PostAuthors } from '../../postElement/SerializerV2/components/PostAuthors';

import { getStructureElement } from '../../serializer/getStructureElement';
import { SerializeTextLeaf, type TTextLeaf } from '../../textLeaf';
import { EditableMergeTagInput } from '../EditableMergeTagInput';
import { EditorSelectableField } from '../EditorSelectableField';
import { WEB_HEADER_SUBTITLE_ATTR, WEB_HEADER_TITLE_ATTR } from '../PostHeader.types';

interface ExtendedSerializerProps extends SerializerProps {
  post: PostHeaderData;
  breadcrumbs: Breadcrumb[];
  node: JSONContent & SerializableNode;
  shareUrl: string;
  isLive?: boolean;
  disableEmbeds?: boolean;
  utmParams?: UTMAttrs;
  publicationTimeZone?: string;
  editorMode?: boolean;
  onTitleChange?: (value: string) => void;
  onSubtitleChange?: (value: string) => void;
  isTitleSelected?: boolean;
  isSubtitleSelected?: boolean;
  renderBoundImage?: (reference: string) => ReactNode;
}

// For draft posts, when post.estimated_reading_time is null
const FALLBACK_READ_TIME_MINUTES = 2;

const serializeNode = (
  {
    node,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    createKey = (entry) => entry[1].join(','),
    children,
    post,
    breadcrumbs,
    isLive,
    disableEmbeds,
    utmParams,
    shareUrl,
    editorMode,
    onTitleChange,
    onSubtitleChange,
    isTitleSelected,
    isSubtitleSelected,
    renderBoundImage,
    publicationTimeZone,
  }: ExtendedSerializerProps,
  path = [0], // internal path tracking
): ReactNode => {
  if (!node) {
    return null;
  }

  const key = createKey([node, path]);
  const dataAttributes = { 'data-json-path': path.join(',') };

  const isImage = node.type === 'image';
  const isParagraph = node.type === 'paragraph';
  const isHeading = node.type === 'heading';
  const isBreadcrumbs = node.type === 'breadcrumbs';
  const isShareElement = node.type === 'postShare';

  if (isShareElement) {
    node = produce(node, (draft) => {
      if (draft.attrs) {
        draft.attrs.data = {
          title: post.web_title,
          url: shareUrl,
        };
      } else {
        draft.attrs = { data: { title: post.web_title, url: shareUrl } };
      }
    });
  }

  // Only show paid content if the post is premium
  if (node.attrs?.mergeTag === 'post.paidContent' && !post.is_premium) {
    return null;
  }

  // Pass breadcrumbs
  if (isBreadcrumbs) {
    node = produce(node, (draft) => {
      if (draft.attrs) {
        draft.attrs.data = { breadcrumbs };
      } else {
        draft.attrs = { data: { breadcrumbs } };
      }
    });
  }

  // Swap out images
  if (isImage) {
    if (node.attrs?.mergeTag !== 'post.image') {
      return null;
    }

    if (post.display_thumbnail_on_web !== true) {
      return null;
    }

    if (editorMode && post.thumbnail_binding && renderBoundImage) {
      return <Fragment key={key}>{renderBoundImage(post.thumbnail_binding)}</Fragment>;
    }

    node = produce(node, (draft) => {
      if (!draft.attrs) {
        draft.attrs = {};
      }

      draft.attrs.src = post?.image_url;
      draft.attrs.alt = post?.web_title;
      draft.attrs.aspectRatio =
        post?.image_width && post?.image_height ? post.image_width / post.image_height : undefined;
    });
  }

  // Swap out headings && paragraphs
  if ((isParagraph || isHeading) && node.attrs?.mergeTag) {
    if (node.attrs.mergeTag === 'post.title') {
      if (editorMode) {
        node = updateText(node, post?.web_title ?? '') as JSONContent & SerializableNode;
        const textLeaf = (node.content?.[0] ?? { type: 'text', text: post?.web_title ?? '' }) as TTextLeaf;

        return (
          <EditorSelectableField key={key} dataAttribute={WEB_HEADER_TITLE_ATTR} selected={isTitleSelected}>
            {getStructureElement({
              node: node as SerializableNode,
              rootClassName,
              rootStyle,
              isImageOptimizable,
              disableEmbeds,
              key,
              children: [
                <EditableMergeTagInput
                  key="title-input"
                  testId="post-title"
                  leaf={textLeaf}
                  defaultValue={post?.web_title ?? ''}
                  placeholder="Add a title..."
                  onChange={onTitleChange}
                />,
              ],
              isLive,
              jsonPath: path,
            })}
          </EditorSelectableField>
        );
      }
      if (post.display_title_on_web === false) {
        return null;
      }
      node = updateText(node, post?.web_title) as JSONContent & SerializableNode;
    }
    if (node.attrs?.mergeTag === 'post.subtitle') {
      if (editorMode) {
        node = updateText(node, post?.web_subtitle ?? '') as JSONContent & SerializableNode;
        const textLeaf = (node.content?.[0] ?? { type: 'text', text: post?.web_subtitle ?? '' }) as TTextLeaf;

        return (
          <EditorSelectableField key={key} dataAttribute={WEB_HEADER_SUBTITLE_ATTR} selected={isSubtitleSelected}>
            {getStructureElement({
              node: node as SerializableNode,
              rootClassName,
              rootStyle,
              isImageOptimizable,
              disableEmbeds,
              key,
              children: [
                <EditableMergeTagInput
                  key="subtitle-input"
                  testId="post-subtitle"
                  leaf={textLeaf}
                  defaultValue={post?.web_subtitle ?? ''}
                  placeholder="Add a subtitle..."
                  onChange={onSubtitleChange}
                />,
              ],
              isLive,
              jsonPath: path,
            })}
          </EditorSelectableField>
        );
      }
      if (!post?.web_subtitle || post.display_subtitle_on_web === false) {
        return null;
      }

      node = updateText(node, post.web_subtitle) as JSONContent & SerializableNode;
    }
    if (node.attrs?.mergeTag === 'post.timestamp') {
      node = updateText(
        node,
        formatPostTime(post?.override_scheduled_at, node.attrs?.postTimestampDisplay, publicationTimeZone),
      ) as JSONContent & SerializableNode;
    }
    if (node.attrs?.mergeTag === 'post.readTime') {
      node = updateText(
        node,
        `${post?.estimated_reading_time || FALLBACK_READ_TIME_MINUTES} ${node.attrs?.postReadTimeSuffix}`,
      ) as JSONContent & SerializableNode;
    }
    if (node?.attrs?.mergeTag === 'post.authors') {
      if (Array.isArray(post?.authors) && post.authors.length === 0) {
        return null;
      }

      return (
        <PostAuthors
          authors={post?.authors || []}
          authorUrls={post?.author_urls}
          node={node}
          utmParams={utmParams}
          attributes={dataAttributes}
          key={key}
        />
      );
    }
    if (node?.attrs?.mergeTag === 'post.tags') {
      return (
        <ContentTags
          tags={post?.content_tags || []}
          node={node}
          key={key}
          isLive={isLive ?? false}
          attributes={dataAttributes}
        />
      );
    }
    if (node?.attrs?.mergeTag === 'post.paidContent') {
      return <PaidContent node={node} isPremium={!post.is_premium || false} key={key} attributes={dataAttributes} />;
    }
  }

  if ('text' in node) {
    return <SerializeTextLeaf key={key} leaf={node as TTextLeaf} />;
  }

  const nodeChildren =
    node.content?.map((n, i) =>
      serializeNode(
        {
          node: n as JSONContent & SerializableNode,
          createKey,
          isImageOptimizable,
          post,
          breadcrumbs,
          isLive,
          disableEmbeds,
          utmParams,
          shareUrl,
          editorMode,
          onTitleChange,
          onSubtitleChange,
          isTitleSelected,
          isSubtitleSelected,
          renderBoundImage,
          publicationTimeZone,
        },
        [...path, i],
      ),
    ) || [];

  return getStructureElement({
    node: node as SerializableNode,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    disableEmbeds,
    key,
    children: [...nodeChildren, children],
    isLive,
    jsonPath: path,
  });
};

export function Serializer(props: ExtendedSerializerProps) {
  const context = useDreamComponentsContext();
  return <>{serializeNode({ ...props, publicationTimeZone: context.publicationTimeZone })}</>;
}
